import { LIST_VIEW, CARD_VIEW, QUERY_KEYS } from "../constants";

let isHydrating = false;
let popstateInitialized = false;

const parseIntOr = (value, fallback) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
};

const isValidIsoDate = (value) => {
  if (!value || typeof value !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

const getHashAndQuery = (locationObj) => {
  const loc = locationObj || window.location;
  const hash = (loc.hash || "").replace(/^#/, "");
  const [hashPath, hashQuery = ""] = hash.split("?");

  const isHashBased = hashPath && /\blearning\/catalogue\b/.test(hashPath);

  if (isHashBased) {
    return { path: hashPath, query: hashQuery };
  } else {
    const searchQuery = (loc.search || "").replace(/^\?/, "");
    return { path: null, query: searchQuery };
  }
};

export const urlStateService = {
  setHydrating(flag) {
    isHydrating = !!flag;
  },
  getIsHydrating() {
    return isHydrating;
  },

  parseStateFromUrl(locationObj) {
    const { path, query } = getHashAndQuery(locationObj);

    const isHashBased = path && /\blearning\/catalogue\b/.test(path);
    const isDirectParams = !path && query;

    if (!isHashBased && !isDirectParams) {
      return null;
    }

    const params = new URLSearchParams(query);
    const hasAny = Array.from(params.keys()).length > 0;
    if (!hasAny) return null;

    const searchText = (params.get(QUERY_KEYS.q) || "").trim();
    const parseMultiInts = (key) => {
      const all = params.getAll(key);
      if (all && all.length > 0) {
        return all
          .map((v) => parseIntOr(v, 0))
          .filter((n) => typeof n === "number" && n !== 0);
      }
      const single = parseIntOr(params.get(key), 0);
      return single ? [single] : [];
    };

    const categoryKeys = [
      QUERY_KEYS.c1,
      QUERY_KEYS.c2,
      QUERY_KEYS.c3,
      QUERY_KEYS.c4,
      QUERY_KEYS.c5,
    ];
    const categoriesValue = categoryKeys.reduce((acc, key, index) => {
      acc[`category${index + 1}`] = parseMultiInts(key);
      return acc;
    }, {});

    const from = params.get(QUERY_KEYS.from);
    const to = params.get(QUERY_KEYS.to);
    const startDate = isValidIsoDate(from) ? from : "";
    const endDate = isValidIsoDate(to) ? to : "";

    const parseMultiStrings = (key) => {
      const all = params.getAll(key);
      if (all && all.length > 0) {
        return all.map((s) => (s || "").trim()).filter((s) => s.length > 0);
      }
      const single = (params.get(key) || "").trim();
      return single ? [single] : [];
    };

    const sessionRegion = parseMultiStrings(QUERY_KEYS.region);
    const sessionCity = parseMultiStrings(QUERY_KEYS.city);

    const sortOrder = parseIntOr(params.get(QUERY_KEYS.sort), 0);

    const view = (params.get(QUERY_KEYS.view) || LIST_VIEW).toLowerCase();
    const listView = view !== CARD_VIEW;

    const page = Math.max(1, parseIntOr(params.get(QUERY_KEYS.page), 1));

    return {
      filters: {
        searchText,
        categoriesValue,
        sortOrder,
        startDate,
        endDate,
        sessionRegion,
        sessionCity,
      },
      uiState: {
        listView,
        page,
      },
    };
  },

  serializeStateToQuery(filters, uiState) {
    const params = new URLSearchParams();

    if (filters.searchText && filters.searchText.trim().length > 0) {
      params.set(QUERY_KEYS.q, filters.searchText.trim());
    }

    const c = filters.categoriesValue || {};
    const writeMulti = (key, val) => {
      if (Array.isArray(val)) {
        val.forEach((n) => {
          if (typeof n === "number" && n !== 0) params.append(key, String(n));
        });
      } else {
        const n = parseIntOr(val, 0);
        if (n) params.append(key, String(n));
      }
    };

    const categoryKeys = [
      QUERY_KEYS.c1,
      QUERY_KEYS.c2,
      QUERY_KEYS.c3,
      QUERY_KEYS.c4,
      QUERY_KEYS.c5,
    ];
    categoryKeys.forEach((key, index) => {
      writeMulti(key, c[`category${index + 1}`]);
    });

    if (filters.startDate && isValidIsoDate(filters.startDate)) {
      params.set(QUERY_KEYS.from, filters.startDate);
    }
    if (filters.endDate && isValidIsoDate(filters.endDate)) {
      params.set(QUERY_KEYS.to, filters.endDate);
    }
    const writeMultiString = (key, val) => {
      if (Array.isArray(val)) {
        val.forEach((s) => {
          const v = (s || "").trim();
          if (v) params.append(key, v);
        });
      } else if (val && typeof val === "string" && val.trim()) {
        params.append(key, val.trim());
      }
    };
    writeMultiString(QUERY_KEYS.region, filters.sessionRegion);
    writeMultiString(QUERY_KEYS.city, filters.sessionCity);

    const sortOrder = parseIntOr(filters.sortOrder, 0);
    if (sortOrder) params.set(QUERY_KEYS.sort, String(sortOrder));

    const view = uiState.listView ? LIST_VIEW : CARD_VIEW;
    params.set(QUERY_KEYS.view, view);

    const page = parseIntOr(uiState.page, 1);
    if (page > 1) params.set(QUERY_KEYS.page, String(page));

    return params.toString();
  },

  isHashBasedRouting() {
    const hash = (window.location.hash || "").replace(/^#/, "");
    const [path] = hash.split("?");
    return path && /\blearning\/catalogue\b/.test(path);
  },

  buildCatalogueUrl(queryString) {
    const { pathname } = window.location;
    const isHashBased = this.isHashBasedRouting();

    if (isHashBased) {
      const base = `${pathname}#/learning/catalogue`;
      return queryString && queryString.length > 0
        ? `${base}?${queryString}`
        : base;
    } else {
      const base = pathname;
      return queryString && queryString.length > 0
        ? `${base}?${queryString}`
        : base;
    }
  },

  preserveExternalParams(newQueryString) {
    const isHashBased = this.isHashBasedRouting();
    let currentQuery = "";

    if (isHashBased) {
      const { query } = getHashAndQuery(window.location);
      currentQuery = query || "";
    } else {
      currentQuery = (window.location.search || "").replace(/^\?/, "");
    }

    if (!currentQuery) {
      return newQueryString;
    }

    const currentParams = new URLSearchParams(currentQuery);
    const newParams = newQueryString
      ? new URLSearchParams(newQueryString)
      : new URLSearchParams();

    const managedKeys = new Set(Object.values(QUERY_KEYS));

    const externalParams = new URLSearchParams();
    currentParams.forEach((value, key) => {
      if (!managedKeys.has(key)) {
        currentParams.getAll(key).forEach((val) => {
          externalParams.append(key, val);
        });
      }
    });

    const combined = new URLSearchParams();

    externalParams.forEach((value, key) => {
      combined.append(key, value);
    });

    newParams.forEach((value, key) => {
      combined.delete(key);
      newParams.getAll(key).forEach((val) => {
        combined.append(key, val);
      });
    });

    return combined.toString();
  },

  replaceUrl(queryString) {
    try {
      const combinedQueryString = this.preserveExternalParams(queryString);
      const href = this.buildCatalogueUrl(combinedQueryString);
      window.history.replaceState(null, "", href);
    } catch {
      // Ignore URL update errors
    }
  },

  initPopState(onParsedState) {
    if (popstateInitialized) return () => {};
    const handler = () => {
      const parsed = this.parseStateFromUrl(window.location);
      if (parsed) {
        onParsedState(parsed);
      }
    };
    window.addEventListener("popstate", handler);
    popstateInitialized = true;
    return () => {
      window.removeEventListener("popstate", handler);
      popstateInitialized = false;
    };
  },
};

export default urlStateService;
